import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiDownload } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import Tutorial from '../components/Tutorial';
import { useSidebar } from '../contexts/SidebarContext';
import { useTutorial } from '../contexts/TutorialContext';
import { authAPI } from '../services/api';
import { API_ENDPOINTS, getToken } from '../config/api';
import './PageTemplate.css';

interface TestRulesProps {
  onLogout?: () => void;
  userEmail?: string;
}

const TestRules: React.FC<TestRulesProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const { showTutorial, currentTutorialStep, completeTutorial, skipTutorial } = useTutorial();
  const [userInfo, setUserInfo] = useState({
    email: userEmail || 'anonymous.user@naati.edu',
    organization: 'NAATI',
    first_name: ''
  });

  useEffect(() => {
    fetchUserProfile();
  }, [userEmail]);

  const fetchUserProfile = async () => {
    try {
      const profile = await authAPI.getProfile();
      setUserInfo({
        email: profile.email || userEmail || 'anonymous.user@naati.edu',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || ''
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };
  const handleDownload = async (filename: string) => {
    try {
      const token = getToken();
      const response = await fetch(API_ENDPOINTS.PDF_DOWNLOAD(filename), {
        method: 'GET',
        headers: {
          Accept: 'application/pdf',
          ...(token ? { Authorization: `Token ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      
      // Check if blob has content
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download ${filename}. Please try again.`);
    }
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="page-title">Test Rules</h1>
              <p className="page-description">Learn about the Persian CCL test rules and regulations</p>
          
          <div className="test-rules-sections">
            {/* CCL Instructions Section */}
            <motion.div
              className="test-rules-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="section-header">
                <h2 className="section-title">Credentialed Community Language Test (CCL) Instructions</h2>
                <motion.button
                  className="download-button"
                  onClick={() => handleDownload('ccl-instructions.pdf')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {React.createElement(FiDownload as React.ComponentType<{ size?: number }>, { size: 18 })}
                  <span>Download PDF</span>
                </motion.button>
              </div>
              <div className="section-content">
                <p>Download the complete CCL test instructions and guidelines to understand the test format, requirements, and procedures.</p>
              </div>
            </motion.div>

            {/* NAATI Identification Section */}
            <motion.div
              className="test-rules-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="section-header">
                <h2 className="section-title">How does NAATI identify you?</h2>
                <motion.button
                  className="download-button"
                  onClick={() => handleDownload('naati-identification.pdf')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {React.createElement(FiDownload as React.ComponentType<{ size?: number }>, { size: 18 })}
                  <span>Download PDF</span>
                </motion.button>
              </div>
              <div className="section-content">
                <p>Learn about NAATI's identification requirements and how they verify your identity during the test.</p>
              </div>
            </motion.div>
          </div>
            </motion.div>
          </div>
        </div>
      </div>
      
      {showTutorial && currentTutorialStep === 2 && (
        <Tutorial
          onComplete={completeTutorial}
          onSkip={skipTutorial}
          initialStep={2}
        />
      )}
    </div>
  );
};

export default TestRules;

